/**
 * Scene/interior template data
 * 32 scene definitions: 21 Unsplash + 11 Midjourney-generated
 */

import type { SceneCategory, HomographyCorners, LightSource } from '@frameit/types';

/** Placement zone within a scene for perspective-correct artwork placement */
export interface ScenePlacementZone {
  /** Zone identifier */
  id: string;
  /** Homography corners defining the wall area (normalized 0-1) */
  corners: HomographyCorners;
  /** Suggested max artwork scale within zone */
  maxScale: number;
}

export interface SceneData {
  id: string;
  name: string;
  category: SceneCategory;
  /** Full background CSS (usually url(...)) */
  background: string;
  /** Thumbnail URL for selector (small image) */
  thumbnail: string;
  /** Dominant wall color for fallback */
  wallColor: string;
  isPremium?: boolean;
  /** Color temperature hint for artwork matching */
  colorTemperature?: 'warm' | 'neutral' | 'cool';
  /** Placement zones where artwork can be placed (perspective-mapped) */
  placementZones?: ScenePlacementZone[];
  /** Light source direction for shadow/highlight matching */
  lightSource?: LightSource;
  /** Group ID for multi-angle scenes (scenes with same groupId are variants of the same room) */
  groupId?: string;
  /** Angle label for grouped scenes (e.g., "Front", "Side", "Wide") */
  angleLabel?: string;
}

/** Scene categories for selector UI */
export const SCENE_CATEGORIES: { id: SceneCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'living-room', label: 'Living' },
  { id: 'bedroom', label: 'Bedroom' },
  { id: 'office', label: 'Office' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'dining-room', label: 'Dining' },
  { id: 'hallway', label: 'Hallway' },
  { id: 'commercial', label: 'Commercial' },
];

/** Helper to build Unsplash URL pair */
function unsplash(photoId: string): { background: string; thumbnail: string } {
  return {
    background: `url(https://images.unsplash.com/${photoId}?w=1920&q=80)`,
    thumbnail: `https://images.unsplash.com/${photoId}?w=200&q=60`,
  };
}

/** Helper for Unsplash+ (premium) photos hosted on plus.unsplash.com */
function unsplashPlus(photoId: string): { background: string; thumbnail: string } {
  return {
    background: `url(https://plus.unsplash.com/${photoId}?w=1920&q=80)`,
    thumbnail: `https://plus.unsplash.com/${photoId}?w=200&q=60`,
  };
}

/** Helper for local scene images (Midjourney-generated) */
function localScene(slug: string): { background: string; thumbnail: string } {
  return {
    background: `url(/scenes/full/${slug}.jpg)`,
    thumbnail: `/scenes/thumb/${slug}.jpg`,
  };
}

// === Placement Zone Presets ===
// Normalized corners (0-1) defining wall areas for common scene compositions.
// These map the quadrilateral on the wall where artwork is placed.

/** Wall above a sofa — upper center, slightly wider than tall */
function wallAboveSofa(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.2, y: 0.05 },
        topRight: { x: 0.8, y: 0.05 },
        bottomRight: { x: 0.8, y: 0.5 },
        bottomLeft: { x: 0.2, y: 0.5 },
      },
      maxScale: 0.7,
    },
  ];
}

/** Wall above a bed — centered, mid-height */
function wallAboveBed(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.25, y: 0.05 },
        topRight: { x: 0.75, y: 0.05 },
        bottomRight: { x: 0.75, y: 0.48 },
        bottomLeft: { x: 0.25, y: 0.48 },
      },
      maxScale: 0.65,
    },
  ];
}

/** Wall above a desk — upper area, slightly narrower */
function wallAboveDesk(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.22, y: 0.05 },
        topRight: { x: 0.78, y: 0.05 },
        bottomRight: { x: 0.78, y: 0.52 },
        bottomLeft: { x: 0.22, y: 0.52 },
      },
      maxScale: 0.65,
    },
  ];
}

/** Full wall — gallery/wall scenes, large usable area */
function fullWall(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.1, y: 0.05 },
        topRight: { x: 0.9, y: 0.05 },
        bottomRight: { x: 0.9, y: 0.9 },
        bottomLeft: { x: 0.1, y: 0.9 },
      },
      maxScale: 0.8,
    },
  ];
}

/** Hallway wall — slightly narrower, taller */
function hallwayWall(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.15, y: 0.05 },
        topRight: { x: 0.85, y: 0.05 },
        bottomRight: { x: 0.85, y: 0.75 },
        bottomLeft: { x: 0.15, y: 0.75 },
      },
      maxScale: 0.7,
    },
  ];
}

/** Wall above a sideboard — wide, mid-height */
function wallAboveSideboard(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.15, y: 0.05 },
        topRight: { x: 0.85, y: 0.05 },
        bottomRight: { x: 0.85, y: 0.48 },
        bottomLeft: { x: 0.15, y: 0.48 },
      },
      maxScale: 0.7,
    },
  ];
}

/** Dining/commercial wall — above table or counter */
function wallAboveTable(): ScenePlacementZone[] {
  return [
    {
      id: 'main',
      corners: {
        topLeft: { x: 0.18, y: 0.05 },
        topRight: { x: 0.82, y: 0.05 },
        bottomRight: { x: 0.82, y: 0.52 },
        bottomLeft: { x: 0.18, y: 0.52 },
      },
      maxScale: 0.65,
    },
  ];
}

// === Light Source Presets ===

function lightFromLeft(): LightSource {
  return {
    type: 'directional',
    color: '#fff8f0',
    intensity: 0.8,
    direction: { x: -1, y: -0.3, z: 0.5 },
  };
}

function lightFromRight(): LightSource {
  return {
    type: 'directional',
    color: '#fff8f0',
    intensity: 0.8,
    direction: { x: 1, y: -0.3, z: 0.5 },
  };
}

function lightFromAbove(): LightSource {
  return {
    type: 'directional',
    color: '#ffffff',
    intensity: 0.7,
    direction: { x: 0, y: -1, z: 0.5 },
  };
}

function lightAmbientWarm(): LightSource {
  return { type: 'ambient', color: '#fff5e6', intensity: 0.6 };
}

function lightAmbientCool(): LightSource {
  return { type: 'ambient', color: '#e6f0ff', intensity: 0.6 };
}

/** All scene definitions — 32 scenes (21 Unsplash + 11 Midjourney) */
export const SCENES: SceneData[] = [
  // === Living Rooms ===
  {
    id: 'scene-beige-sofa',
    name: 'Beige Sofa',
    category: 'living-room',
    ...unsplash('photo-1555041469-a586c61ea9bc'),
    wallColor: '#f5f5f5',
    colorTemperature: 'warm',
    placementZones: wallAboveSofa(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-modern-living',
    name: 'Modern Living',
    category: 'living-room',
    ...unsplash('photo-1618221195710-dd6b41faaea6'),
    wallColor: '#ffffff',
    colorTemperature: 'neutral',
    placementZones: wallAboveSofa(),
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-scandinavian',
    name: 'Scandinavian',
    category: 'living-room',
    ...unsplash('photo-1616137422495-1e9e46e2aa77'),
    wallColor: '#f5f5f5',
    colorTemperature: 'neutral',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.3, y: 0.05 },
          topRight: { x: 0.9, y: 0.08 },
          bottomRight: { x: 0.88, y: 0.52 },
          bottomLeft: { x: 0.28, y: 0.5 },
        },
        maxScale: 0.65,
      },
    ],
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-yellow-sofas',
    name: 'Yellow Sofas',
    category: 'living-room',
    ...unsplash('photo-1770118953174-efcec988390e'),
    wallColor: '#c8ccd0',
    colorTemperature: 'cool',
    placementZones: fullWall(),
    lightSource: lightFromRight(),
  },

  // === Bedrooms ===
  {
    id: 'scene-white-bed',
    name: 'White Bed',
    category: 'bedroom',
    ...unsplash('photo-1505693416388-ac5ce068fe85'),
    wallColor: '#ffffff',
    colorTemperature: 'neutral',
    placementZones: wallAboveBed(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-grey-bed',
    name: 'Grey Bedroom',
    category: 'bedroom',
    ...unsplash('photo-1522771739844-6a9f6d5f14af'),
    wallColor: '#d0d0d0',
    colorTemperature: 'cool',
    placementZones: wallAboveBed(),
    lightSource: lightAmbientCool(),
  },
  {
    id: 'scene-minimal-bed',
    name: 'Minimal Bed',
    category: 'bedroom',
    ...unsplash('photo-1588046130717-0eb0c9a3ba15'),
    wallColor: '#f5f5f5',
    colorTemperature: 'neutral',
    placementZones: wallAboveBed(),
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-master-bedroom',
    name: 'Master Bedroom',
    category: 'bedroom',
    ...unsplash('photo-1560185893-a55cbc8c57e8'),
    wallColor: '#f0f0f0',
    colorTemperature: 'neutral',
    placementZones: wallAboveBed(),
    lightSource: lightFromLeft(),
  },

  // === Office ===
  {
    id: 'scene-simple-desk',
    name: 'Simple Desk',
    category: 'office',
    ...unsplash('photo-1518455027359-f3f8164ba6bd'),
    wallColor: '#ffffff',
    colorTemperature: 'neutral',
    placementZones: wallAboveDesk(),
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-bookshelf',
    name: 'Nordic Study',
    category: 'office',
    ...unsplash('photo-1449247709967-d4461a6a6103'),
    wallColor: '#ffffff',
    colorTemperature: 'neutral',
    placementZones: wallAboveDesk(),
    lightSource: lightFromAbove(),
  },

  // === Gallery / Walls ===
  {
    id: 'scene-concrete-wall',
    name: 'Nordic Wall',
    category: 'gallery',
    ...unsplash('photo-1511389026070-a14ae610a1be'),
    wallColor: '#f0f0f0',
    colorTemperature: 'neutral',
    placementZones: fullWall(),
    lightSource: lightAmbientCool(),
  },
  {
    id: 'scene-museum-wall',
    name: 'Gallery Room',
    category: 'gallery',
    ...unsplash('photo-1543936177-12e24c26776a'),
    wallColor: '#f5f0e8',
    colorTemperature: 'warm',
    placementZones: fullWall(),
    lightSource: lightAmbientWarm(),
  },
  {
    id: 'scene-textured-wall',
    name: 'Textured Wall',
    category: 'gallery',
    ...unsplash('photo-1534889156217-d643df14f14a'),
    wallColor: '#d0c8b8',
    colorTemperature: 'warm',
    placementZones: fullWall(),
    lightSource: lightAmbientWarm(),
  },
  {
    id: 'scene-gallery-bench',
    name: 'Gallery Bench',
    category: 'gallery',
    ...unsplash('photo-1609740700123-1f8e327cb18f'),
    wallColor: '#e8e8e8',
    colorTemperature: 'neutral',
    placementZones: fullWall(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-gallery-concrete',
    name: 'Concrete Gallery',
    category: 'gallery',
    ...unsplash('photo-1757138578631-1323a72b5f21'),
    wallColor: '#d8d4d0',
    colorTemperature: 'neutral',
    placementZones: fullWall(),
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-gallery-chair',
    name: 'Gallery Chair',
    category: 'gallery',
    ...unsplash('photo-1620910424861-69c7706d3df8'),
    wallColor: '#e0dcd8',
    colorTemperature: 'neutral',
    placementZones: fullWall(),
    lightSource: lightFromRight(),
  },

  // === Dining ===
  {
    id: 'scene-round-table',
    name: 'Round Table',
    category: 'dining-room',
    ...unsplash('photo-1549187774-b4e9b0445b41'),
    wallColor: '#ffffff',
    colorTemperature: 'neutral',
    placementZones: wallAboveTable(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-sunlit-dining',
    name: 'Sunlit Dining',
    category: 'dining-room',
    ...unsplash('photo-1770646403987-64cf5c08c870'),
    wallColor: '#f0ece4',
    colorTemperature: 'warm',
    placementZones: wallAboveTable(),
    lightSource: lightFromLeft(),
  },

  // === Hallway ===
  {
    id: 'scene-hallway-white',
    name: 'White Hallway',
    category: 'hallway',
    ...unsplash('photo-1513694203232-719a280e022f'),
    wallColor: '#f5f5f5',
    colorTemperature: 'neutral',
    placementZones: hallwayWall(),
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-entryway',
    name: 'Nordic Entry',
    category: 'hallway',
    ...unsplash('photo-1639690440445-dd7c8ab2d8af'),
    wallColor: '#f0f0ec',
    colorTemperature: 'warm',
    placementZones: hallwayWall(),
    lightSource: lightFromLeft(),
  },

  // === Commercial ===
  {
    id: 'scene-cafe-modern',
    name: 'Modern Cafe',
    category: 'commercial',
    ...unsplashPlus('premium_photo-1727456097893-50777a5c086c'),
    wallColor: '#d8d0c8',
    colorTemperature: 'warm',
    placementZones: fullWall(),
    lightSource: lightFromAbove(),
  },

  // === Midjourney Scenes ===

  // Living Rooms
  {
    id: 'scene-mj-scandinavian-living',
    name: 'Nordic Living',
    category: 'living-room',
    ...localScene('scandinavian-modern-living-room-interior'),
    wallColor: '#f0ede8',
    colorTemperature: 'neutral',
    placementZones: wallAboveSofa(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-mj-luxury-dark',
    name: 'Dark Luxury',
    category: 'living-room',
    ...localScene('luxury-dark-moody-living-room'),
    wallColor: '#2a2a30',
    colorTemperature: 'warm',
    placementZones: wallAboveSofa(),
    lightSource: lightAmbientWarm(),
  },
  {
    id: 'scene-mj-bohemian',
    name: 'Bohemian',
    category: 'living-room',
    ...localScene('bohemian-style-living-room'),
    wallColor: '#b05a2a',
    colorTemperature: 'warm',
    placementZones: wallAboveSideboard(),
    lightSource: lightFromRight(),
  },

  // Bedrooms
  {
    id: 'scene-mj-modern-bedroom',
    name: 'Modern Bedroom',
    category: 'bedroom',
    ...localScene('modern-minimalist-bedroom'),
    wallColor: '#e8e4e0',
    colorTemperature: 'neutral',
    placementZones: wallAboveBed(),
    lightSource: lightFromLeft(),
  },
  {
    id: 'scene-mj-cozy-bedroom',
    name: 'Cozy Bedroom',
    category: 'bedroom',
    ...localScene('cozy-scandinavian-bedroom'),
    wallColor: '#d8c8a8',
    colorTemperature: 'warm',
    placementZones: wallAboveBed(),
    lightSource: lightFromLeft(),
  },

  // Gallery
  {
    id: 'scene-mj-white-gallery',
    name: 'White Gallery',
    category: 'gallery',
    ...localScene('white-gallery-wall'),
    wallColor: '#f5f5f5',
    colorTemperature: 'neutral',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.2, y: 0.05 },
          topRight: { x: 0.75, y: 0.05 },
          bottomRight: { x: 0.75, y: 0.85 },
          bottomLeft: { x: 0.2, y: 0.85 },
        },
        maxScale: 0.8,
      },
    ],
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-mj-concrete-gallery',
    name: 'Brutalist Gallery',
    category: 'gallery',
    ...localScene('raw-concrete-wall'),
    wallColor: '#888888',
    colorTemperature: 'neutral',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.08, y: 0.05 },
          topRight: { x: 0.92, y: 0.05 },
          bottomRight: { x: 0.92, y: 0.7 },
          bottomLeft: { x: 0.08, y: 0.7 },
        },
        maxScale: 0.75,
      },
    ],
    lightSource: lightFromAbove(),
  },

  // Office
  {
    id: 'scene-mj-home-office',
    name: 'Green Office',
    category: 'office',
    ...localScene('modern-home-office'),
    wallColor: '#8faa7e',
    colorTemperature: 'neutral',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.1, y: 0.05 },
          topRight: { x: 0.6, y: 0.05 },
          bottomRight: { x: 0.6, y: 0.5 },
          bottomLeft: { x: 0.1, y: 0.5 },
        },
        maxScale: 0.6,
      },
    ],
    lightSource: lightFromLeft(),
  },

  // Dining
  {
    id: 'scene-mj-dining',
    name: 'Minimal Dining',
    category: 'dining-room',
    ...localScene('modern-dining-room'),
    wallColor: '#e8e0d8',
    colorTemperature: 'neutral',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.25, y: 0.05 },
          topRight: { x: 0.85, y: 0.05 },
          bottomRight: { x: 0.85, y: 0.5 },
          bottomLeft: { x: 0.25, y: 0.5 },
        },
        maxScale: 0.65,
      },
    ],
    lightSource: lightFromLeft(),
  },

  // Commercial
  {
    id: 'scene-mj-cafe',
    name: 'Brick Cafe',
    category: 'commercial',
    ...localScene('stylish-modern-cafe'),
    wallColor: '#8b5a3a',
    colorTemperature: 'warm',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.15, y: 0.05 },
          topRight: { x: 0.85, y: 0.05 },
          bottomRight: { x: 0.85, y: 0.6 },
          bottomLeft: { x: 0.15, y: 0.6 },
        },
        maxScale: 0.65,
      },
    ],
    lightSource: lightFromAbove(),
  },
  {
    id: 'scene-mj-hotel-lobby',
    name: 'Hotel Lobby',
    category: 'commercial',
    ...localScene('luxury-hotel-lobby'),
    wallColor: '#6b5a40',
    colorTemperature: 'warm',
    placementZones: [
      {
        id: 'main',
        corners: {
          topLeft: { x: 0.15, y: 0.05 },
          topRight: { x: 0.85, y: 0.05 },
          bottomRight: { x: 0.85, y: 0.6 },
          bottomLeft: { x: 0.15, y: 0.6 },
        },
        maxScale: 0.65,
      },
    ],
    lightSource: lightAmbientWarm(),
  },
];

/** Get scene background image URL by scene ID */
export function getSceneBackgroundUrl(sceneId: string): string | undefined {
  const scene = SCENES.find((s) => s.id === sceneId);
  if (!scene) return undefined;
  const match = scene.background.match(/url\((.+)\)/);
  return match ? match[1] : undefined;
}

/** Get scene data by ID */
export function getSceneData(sceneId: string): SceneData | undefined {
  return SCENES.find((s) => s.id === sceneId);
}

/** Get placement zone for a scene (defaults to centered wall) */
export function getScenePlacementZone(
  sceneId: string,
  zoneId: string = 'main',
): ScenePlacementZone | undefined {
  const scene = SCENES.find((s) => s.id === sceneId);
  if (!scene?.placementZones) return undefined;
  return scene.placementZones.find((z) => z.id === zoneId);
}

/** Default placement zone when scene has no specific zone defined */
export const DEFAULT_PLACEMENT_ZONE: ScenePlacementZone = {
  id: 'main',
  corners: {
    topLeft: { x: 0.15, y: 0.1 },
    topRight: { x: 0.85, y: 0.1 },
    bottomRight: { x: 0.85, y: 0.8 },
    bottomLeft: { x: 0.15, y: 0.8 },
  },
  maxScale: 0.7,
};

/** Get all angle variants for a grouped scene */
export function getSceneAngleVariants(sceneId: string): SceneData[] {
  const scene = SCENES.find((s) => s.id === sceneId);
  if (!scene?.groupId) return [scene].filter(Boolean) as SceneData[];
  return SCENES.filter((s) => s.groupId === scene.groupId);
}

/** Get unique scenes for the grid (one per group, plus ungrouped) */
export function getUniqueScenes(): SceneData[] {
  const seen = new Set<string>();
  return SCENES.filter((s) => {
    if (!s.groupId) return true;
    if (seen.has(s.groupId)) return false;
    seen.add(s.groupId);
    return true;
  });
}

/** Get scene preset by ID */
export function getScenePreset(sceneId: string): SceneData | undefined {
  return SCENES.find((s) => s.id === sceneId);
}

/** Scene backgrounds lookup for export renderer */
export const SCENE_BACKGROUNDS: Record<string, string> = Object.fromEntries(
  SCENES.map((s) => {
    const match = s.background.match(/url\((.+)\)/);
    return [s.id, match ? match[1] : ''];
  }).filter(([, url]) => url),
);
